import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { SEO } from "../components/SEO";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Users, ArrowLeftRight, Trophy, Target, Calendar, X, ArrowRight } from "lucide-react";
import { Input } from "../components/ui/input";

/**
 * Player Comparison — /players/compare
 * Two search-driven pickers side-by-side. Load full player details for the
 * two selections and render a comparison grid. Selections are reflected in
 * ?a=slug&b=slug so the page is shareable.
 */
export default function PlayerComparePage() {
  const [params, setParams] = useSearchParams();
  const [allPlayers, setAllPlayers] = useState([]); // for the picker
  const [loadingList, setLoadingList] = useState(true);
  const [playerA, setPlayerA] = useState(null);
  const [playerB, setPlayerB] = useState(null);

  const slugA = params.get("a") || "";
  const slugB = params.get("b") || "";

  // Fetch the picker list once (paginated call, up to 1000 via all_results).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/players?all_results=true`);
        if (!cancelled) setAllPlayers(res.data.players || []);
      } catch {
        // silent — picker just stays empty
      } finally {
        if (!cancelled) setLoadingList(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch full player detail when a slug is selected.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!slugA) {
        setPlayerA(null);
        return;
      }
      try {
        const res = await axios.get(`${API}/players/by-slug/${slugA}`);
        if (!cancelled) setPlayerA(res.data);
      } catch {
        if (!cancelled) setPlayerA(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slugA]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!slugB) {
        setPlayerB(null);
        return;
      }
      try {
        const res = await axios.get(`${API}/players/by-slug/${slugB}`);
        if (!cancelled) setPlayerB(res.data);
      } catch {
        if (!cancelled) setPlayerB(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slugB]);

  const selectPlayer = (side, slug) => {
    const next = new URLSearchParams(params);
    if (slug) next.set(side, slug);
    else next.delete(side);
    setParams(next, { replace: false });
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="player-compare-page">
      <SEO
        title={
          playerA && playerB
            ? `${playerA.name} vs ${playerB.name} — Player Comparison`
            : "Player Comparison"
        }
        path="/players/compare"
        description="Compare any two West Ham United players side by side. See goals, appearances, positions, era and career highlights across every Hammer from 1895 to today."
      />
      <Navbar />

      <div className="bg-gradient-to-r from-[#7C2C3B] to-[#2BAEE5] py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4">
            <ArrowLeftRight className="w-10 h-10 text-yellow-400" />
            <div>
              <h1
                className="text-4xl md:text-5xl font-bold text-white"
                style={{ fontFamily: "Barlow Condensed" }}
              >
                PLAYER COMPARISON
              </h1>
              <p className="text-white/80">Pick any two Hammers and put them side by side</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <PlayerPickerCard
            side="a"
            selected={playerA}
            allPlayers={allPlayers}
            loading={loadingList}
            excludeSlug={slugB}
            onSelect={(slug) => selectPlayer("a", slug)}
          />
          <PlayerPickerCard
            side="b"
            selected={playerB}
            allPlayers={allPlayers}
            loading={loadingList}
            excludeSlug={slugA}
            onSelect={(slug) => selectPlayer("b", slug)}
          />
        </div>

        {playerA && playerB && (
          <ComparisonGrid a={playerA} b={playerB} />
        )}

        {(!playerA || !playerB) && !loadingList && (
          <p className="mt-10 text-center text-slate-500 text-sm" data-testid="compare-hint">
            Pick a player on each side to see the comparison.
          </p>
        )}
      </div>

      <Footer />
    </div>
  );
}

function PlayerPickerCard({ side, selected, allPlayers, loading, excludeSlug, onSelect }) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return allPlayers
      .filter((p) => p.slug !== excludeSlug)
      .filter((p) => (p.name || "").toLowerCase().includes(q))
      .slice(0, 8);
  }, [allPlayers, query, excludeSlug]);

  return (
    <div
      className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm"
      data-testid={`picker-card-${side}`}
    >
      {selected ? (
        <div className="flex items-start gap-4">
          <div className="w-20 h-20 rounded-full overflow-hidden bg-slate-100 flex-shrink-0 border border-slate-200">
            {selected.image_url ? (
              <img
                src={selected.image_url}
                alt=""
                className="w-full h-full object-cover"
                onError={(e) => { e.target.style.display = "none"; }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate-400">
                <Users className="w-8 h-8" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase font-semibold text-[#7C2C3B]">
              Player {side.toUpperCase()}
            </p>
            <h3
              className="text-2xl font-bold text-slate-900 truncate"
              style={{ fontFamily: "Barlow Condensed" }}
              data-testid={`selected-name-${side}`}
            >
              {selected.name}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {(selected.positions || []).join(" · ") || selected.position || "—"}
            </p>
            <div className="flex items-center gap-3 mt-2">
              <Link
                to={`/players/${selected.slug}`}
                className="text-xs font-semibold text-[#7C2C3B] hover:underline"
              >
                View profile →
              </Link>
              <button
                onClick={() => onSelect("")}
                className="text-xs text-slate-400 hover:text-red-600 flex items-center gap-1"
                data-testid={`clear-${side}`}
              >
                <X className="w-3 h-3" /> Change
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <p className="text-xs uppercase font-semibold text-[#7C2C3B] mb-2">
            Pick Player {side.toUpperCase()}
          </p>
          <Input
            placeholder={loading ? "Loading players…" : "Search a Hammer by name…"}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            disabled={loading}
            data-testid={`search-${side}`}
          />
          {filtered.length > 0 && (
            <ul className="mt-2 border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-56 overflow-y-auto bg-white">
              {filtered.map((p) => (
                <li key={p.slug}>
                  <button
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between gap-2"
                    onClick={() => { onSelect(p.slug); setQuery(""); }}
                    data-testid={`pick-${side}-${p.slug}`}
                  >
                    <span>
                      <span className="font-semibold text-slate-900">{p.name}</span>
                      {p.years_active && (
                        <span className="text-xs text-slate-500 ml-2">
                          {p.years_active}
                        </span>
                      )}
                    </span>
                    <ArrowRight className="w-4 h-4 text-slate-400" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {query && filtered.length === 0 && !loading && (
            <p className="text-xs text-slate-400 mt-2">No matches for "{query}"</p>
          )}
        </div>
      )}
    </div>
  );
}

function ComparisonGrid({ a, b }) {
  const rows = [
    {
      label: "Position(s)",
      icon: Target,
      valA: (a.positions || []).join(", ") || a.position || "—",
      valB: (b.positions || []).join(", ") || b.position || "—",
    },
    {
      label: "Era",
      icon: Calendar,
      valA: a.years_active || "—",
      valB: b.years_active || "—",
    },
    {
      label: "Team",
      icon: Users,
      valA: a.team || "—",
      valB: b.team || "—",
    },
    {
      label: "Appearances",
      icon: Users,
      valA: a.appearances ?? "—",
      valB: b.appearances ?? "—",
      numeric: true,
    },
    {
      label: "Goals",
      icon: Trophy,
      valA: a.goals ?? "—",
      valB: b.goals ?? "—",
      numeric: true,
    },
  ];

  // Numeric comparison highlights (higher wins)
  const winnerFor = (row) => {
    if (!row.numeric) return null;
    const nA = Number(row.valA);
    const nB = Number(row.valB);
    if (!Number.isFinite(nA) || !Number.isFinite(nB)) return null;
    if (nA === nB) return "tie";
    return nA > nB ? "a" : "b";
  };

  return (
    <div className="mt-8 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="comparison-grid">
      <div className="grid grid-cols-3 bg-slate-50 border-b border-slate-200 text-center py-3">
        <div className="text-sm font-bold text-slate-500 uppercase tracking-wider">Stat</div>
        <div
          className="text-sm font-bold text-[#7C2C3B] truncate px-2"
          style={{ fontFamily: "Barlow Condensed", fontSize: "1.1rem" }}
        >
          {a.name}
        </div>
        <div
          className="text-sm font-bold text-[#2BAEE5] truncate px-2"
          style={{ fontFamily: "Barlow Condensed", fontSize: "1.1rem" }}
        >
          {b.name}
        </div>
      </div>
      <div className="divide-y divide-slate-100">
        {rows.map((row) => {
          const winner = winnerFor(row);
          const Icon = row.icon;
          return (
            <div key={row.label} className="grid grid-cols-3 items-center py-3 text-center">
              <div className="flex items-center justify-center gap-2 text-xs uppercase text-slate-500 font-semibold">
                <Icon className="w-4 h-4 text-slate-400" />
                {row.label}
              </div>
              <div
                className={`text-lg font-bold px-2 ${
                  winner === "a" ? "text-green-700" : "text-slate-800"
                }`}
                data-testid={`stat-${row.label.toLowerCase().replace(/[^a-z0-9]/g, "-")}-a`}
              >
                {row.valA}
                {winner === "a" && <span className="ml-1 text-xs">▲</span>}
              </div>
              <div
                className={`text-lg font-bold px-2 ${
                  winner === "b" ? "text-green-700" : "text-slate-800"
                }`}
                data-testid={`stat-${row.label.toLowerCase().replace(/[^a-z0-9]/g, "-")}-b`}
              >
                {row.valB}
                {winner === "b" && <span className="ml-1 text-xs">▲</span>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Career highlights side-by-side */}
      {(a.career_summary || b.career_summary || a.biography || b.biography) && (
        <div className="grid grid-cols-1 md:grid-cols-2 border-t border-slate-200 divide-y md:divide-y-0 md:divide-x divide-slate-200">
          <div className="p-5">
            <p className="text-xs uppercase font-semibold text-[#7C2C3B] mb-2">
              Career Highlights — {a.name}
            </p>
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
              {a.career_summary || a.biography || "No career notes yet — check back soon."}
            </p>
          </div>
          <div className="p-5">
            <p className="text-xs uppercase font-semibold text-[#2BAEE5] mb-2">
              Career Highlights — {b.name}
            </p>
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
              {b.career_summary || b.biography || "No career notes yet — check back soon."}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
